import { AppLogo } from "components/ui/shared/AppLogo";
import styled from "styled-components";

import { ReactComponent as DragHandleIcon } from 'assets/icons/drag_handle_current_color_300.svg';
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import React, { useEffect, useRef, useState } from "react";
import { useApi } from "providers/UserProvider";
import { DragHandle } from '@tiptap/extension-drag-handle-react'
import TextAlign from "@tiptap/extension-text-align";

import { EditorHistoryTray } from "components/ui/inputs/toolbars/EditorHistoryTray";
import { EditorHeadingTray } from "components/ui/inputs/toolbars/EditorHeadingTray";
import { EditorFormatTray } from "components/ui/inputs/toolbars/EditorFormatTray";
import { EditorColorsTray } from "components/ui/inputs/toolbars/EditorColorsTray";
import { EditorTextAlignTray } from "components/ui/inputs/toolbars/EditorTextAlignTray";
import { EditorListTray } from "components/ui/inputs/toolbars/EditorListTray";
import { Table, TableHeader, TableRow } from "@tiptap/extension-table";
import { CustomImage, CustomTableCell, getImageFromClipboard, getTableCellBackground, handleImagePaste, isCellMerged, isInTable, TableCellUI } from "utilities/editorUtilities";
import { EditorTableTray } from "components/ui/inputs/toolbars/EditorTableTray";
import { useNavigate, useParams } from "react-router-dom";
import { useErrors } from "providers/ErrorsProvider";
import { parseDateToTimestamp } from "utilities/dateAndTime";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";


export const EditorContext = React.createContext(null);

export const NoteEditor = () => {

    const { noteUuid } = useParams();
    const navigate = useNavigate();
    const { api } = useApi();
    const { setError } = useErrors();
    
    
    const [metadata, setMetadata] = useState({
        title: '',
        titleVersion: 0,
        
        contentSummary: '',
        contentVersion: 0,

        timestamp: null,
        status: null,
        folderName: null
    });

    const [loading, setLoading] = useState(true);
    const [previousValidTitle, setPreviousValidTitle] = useState('');

    const contentRef = useRef(null);
    const contentSummaryRef = useRef(null);
    const saveTimeout = useRef(null);
    const contentLoadedRef = useRef(false);
    const isApplyingServerUpdateRef = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ table: false }),
            TextStyleKit,
            Highlight.configure({  multicolor: true }),
            CustomImage,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Table.configure({
                renderWrapper: true,
                resizable: true,
                cellMinWidth: 50,
            }),
            TableRow,
            CustomTableCell,
            TableHeader,
            TableCellUI
        ],

        content: '',

        editorProps: {
            handlePaste: (_, event) => {
                const images = getImageFromClipboard(event);

                if (!images.length) return false;

                event.preventDefault();

                images.forEach(file => {
                    handleImagePaste(api, file, editor)
                });

                return true;
            }
        },

        onUpdate: ({ editor }) => {
            contentRef.current = editor.getJSON();
            contentSummaryRef.current = editor.getText({
                    blockSeparator: ' '
                })
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 240);
            queueSave();
        }
    });

    const queueSave = () => {
        if (isApplyingServerUpdateRef.current) return;
        if(saveTimeout.current) clearTimeout(saveTimeout.current);
        if (!contentLoadedRef.current) return;
    

        saveTimeout.current = setTimeout(async () => {
            isApplyingServerUpdateRef.current = true;

            try
            {
                const payload = {
                    noteUuid,
                    noteTitle: metadata.title,
                    titleVersion: metadata.titleVersion,
                    noteContent: contentRef.current,
                    noteContentSummary: contentSummaryRef.current,
                    noteContentVersion: metadata.contentVersion,
                    noteStatus: metadata.status
                }

                const res = await api.request("notes/update/content", payload)
                if(res.reason === "NOTE_TITLE_UPDATED_CONTENT_UPDATED")
                {
                    setMetadata(prev => ({
                        ...prev,
                        titleVersion: res.noteTitleVersion,
                        contentVersion: res.noteContentVersion,
                        timestamp: res.noteTimestamp
                    }));
                }
            }
            catch(err)
            {
                if(!err || !err.body || !err.body.reason) return;
                const errContent = err.body;
                
                if(errContent)
                {
                    setError(errContent.reason);
                    if(errContent.reason === "NOTE_CONTENT_ARCHIVED")
                    {
                        setMetadata(prev => ({
                            ...prev,
                            status: errContent.noteStatus,
                            title: errContent.noteTitle,
                            titleVersion: errContent.noteTitleVersion,
                            contentVersion: errContent.noteContentVersion,
                            timestamp: errContent.noteTimestamp
                        }));

                        editor.commands.setContent(errContent.noteContent);
                    }
                    else if(errContent.reason === "NOTE_TITLE_UPDATED_CONTENT_CONFLICT")
                    {
                        setMetadata(prev => ({
                            ...prev,
                            titleVersion: errContent.noteTitleVersion,
                            contentVersion: errContent.noteContentVersion,
                            timestamp: errContent.noteTimestamp
                        }));

                        editor.commands.setContent(errContent.noteContent);
                    }
                    else if(errContent.reason === "NOTE_TITLE_CONFLICT_CONTENT_UPDATED")
                    {
                        setMetadata(prev => ({
                            ...prev,
                            title: errContent.noteTitle,
                            titleVersion: errContent.noteTitleVersion,
                            contentVersion: errContent.noteContentVersion,
                            timestamp: errContent.noteTimestamp
                        }));
                    }
                    else if(errContent.reason === "NOTE_TITLE_CONFLICT_CONTENT_CONFLICT")
                    {
                        setMetadata(prev => ({
                            ...prev,
                            title: errContent.noteTitle,
                            titleVersion: errContent.noteTitleVersion,
                            contentVersion: errContent.noteContentVersion,
                            timestamp: errContent.noteTimestamp
                        }));
                        editor.commands.setContent(errContent.noteContent);
                    }
                }
            }
            finally
            {
                 requestAnimationFrame(() => {
                    isApplyingServerUpdateRef.current = false;
                });
            }
        }, 1000)
    }

    useEffect(() => {
        const loadDocument = async () => {
            try
            {
                const res = await api.request('notes/query/content', {noteUuid});
                setMetadata({
                    title: res.note.noteTitle,
                    titleVersion: res.note.noteTitleVersion,

                    contentSummary: res.note.noteContentSummary,
                    contentVersion: res.note.noteContentVersion,

                    timestamp: new Date(res.note.noteTimestamp),
                    status: res.note.noteStatus,
                    folderName: res.note.folderName,
                });

                if(editor && !contentLoadedRef.current)
                { 
                    editor.commands.setContent(res.note.noteContent || '', false);
                    contentRef.current = res.note.noteContent;
                    contentSummaryRef.current = res.note.noteContentSummary;
                    contentLoadedRef.current = true;
                }

            }
            catch(err)
            {
                console.error(err)
                
                if(err?.status === 404 && err?.body?.reason === "NOTE_NOT_FOUND") navigate('/notes');
            }
            finally
            {
                setLoading(false);
            }
        }

        loadDocument();
    }, [noteUuid, editor, api, navigate])

    /*
    const { noteUuid } = useParams();
    const navigate = useNavigate();
    const { api } = useApi();

    const [documentContent, setDocumentContent] = useState({
        title: '',
        titleVersion: 0,

        content: '',
        contentSummary: '',
        contentVersion: 0,

        parentUuid: null,
        parentName: null,

        timestamp: null
    });

    const [contentReady, setContentReady] = useState(false);
    const [loading, setLoading] = useState(true);

    const contentRef = useRef(null);
    const saveTimeout = useRef(null);

    useEffect(() => {
        const loadDocument = async () => {
            try
            {
                const res = await api.request('notes/query/content', {noteUuid});
                
                setDocumentContent({
                    title: res.note.noteTitle,
                    titleVersion: res.note.noteTitleVersion,

                    content: res.note.noteContent,
                    contentSummary: res.note.noteContentSummary,
                    contentVersion: res.note.noteContentVersion,

                    timestamp: new Date(res.note.noteTimestamp),
                    status: res.note.noteStatus,
                    folderName: res.note.folderName,
                });


                contentRef.current = res.noteContent;

                setContentReady(true);
            }
            catch(err)
            {
                console.error(err);
                if(err?.status === 404 && err?.body?.reason === "NOTE_NOT_FOUND") navigate('/notes');
            }
            finally
            {
                setLoading(false);
            }
        }

        loadDocument();
    }, [noteUuid])

    const queueSave = () => {
        if(saveTimeout.current) clearTimeout(saveTimeout.current)

        saveTimeout.current = setTimeout(() => {
            const snapshot = {
            title: documentContent.title,
            content: contentRef.current,
            }

            console.log(snapshot)

            setDocumentContent(prev => ({
                ...prev,
                content: contentRef.current,
            }))
        }, 1000)
    }
    
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                table: false
            }),
            TextStyleKit,

            Highlight.configure({
                multicolor: true
            }),

            CustomImage,

            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),

            Table.configure({
                renderWrapper: true,
                resizable: true,
                cellMinWidth: 50,
            }),

            TableRow,
            CustomTableCell,
            TableHeader,
            TableCellUI
        ],

        content: '',

        editorProps: {
            handlePaste: (_, event) => {
                const images = getImageFromClipboard(event);

                if (!images.length) return false;

                event.preventDefault();

                images.forEach(file => {
                    handleImagePaste(api, file, editor)
                });

                return true;
            }
        },

        onUpdate: ({ editor }) => {
            contentRef.current = editor.getJSON();
            queueSave();
        }
    });

    let isResizing = false

    editor.view.dom.addEventListener('mousedown', (e) => {
    if (e.target.closest?.('.column-resize-handle')) {
        isResizing = true
    }
    })


    useEffect(() => {
        if (!editor || !documentContent.content) return;
        editor.commands.setContent(documentContent.content, false);
    }, [editor, documentContent.content]);
    */

    const editorState = useEditorState({
        editor,
        selector: ({editor}) => ({
            canUndo: editor.can().chain().focus().undo().run(),
            canRedo: editor.can().chain().focus().redo().run(),

            headingLevel: editor.isActive('heading') ? 'h' + editor?.getAttributes('heading').level : 'paragraph',
            isBold: editor.isActive('bold'),
            isItalic: editor.isActive('italic'),
            isUnderline: editor.isActive('underline'),
            isStrike: editor.isActive('strike'),

            currentColor: editor.getAttributes('textStyle').color,
            currentHighlight: editor.getAttributes('highlight').color,

            currentAlignment:
                editor.isActive({ textAlign: 'right' }) ? 'right' :
                editor.isActive({ textAlign: 'center'}) ? 'center' :
                editor.isActive({ textAlign: 'justify'}) ? 'justify' : 'left',

            currentList:
                editor.isActive('bulletList') ? 'bulleted' :
                editor.isActive('orderedList') ? 'ordered' : 'none',

            isCellMerged: isCellMerged(editor),
            isInTable: isInTable(editor),
            currentCellBackground: getTableCellBackground(editor)
        }) 
    })

    return (
        <EditorContext.Provider value={{editor, editorState}}>
            <StyledNoteEditor>
                <div className="editorHeader">
                    <AppLogo excludeText/>
                    <div className="headerNoteTitle">
                        <div>
                            {metadata.folderName}
                        </div>
                        <input
                            value={metadata.title}
                            onFocus={() => setPreviousValidTitle(metadata.title)}
                            onChange={(e) => {
                                setMetadata(prev => ({...prev, title: e.target.value}))
                            }}
                            onBlur={() => {
                                if(metadata.title.length < 5)
                                {
                                    setMetadata(prev => ({...prev, title: previousValidTitle}));
                                    setError("NOTES_NOTETITLE_TOO_SHORT");
                                }
                                else if(metadata.title.length > 100)
                                {
                                    setMetadata(prev => ({...prev, title: previousValidTitle}));
                                    setError("NOTES_NOTETITLE_TOO_LONG");
                                }
                                    
                                queueSave()
                            }}
                        />
                    </div>
                    <div className="cloudInfo">
                        <MaterialIcon
                            name='cloud'
                            weight={300}
                            color={({theme}) => theme.colors.mutedForeground}
                        />
                        <div>
                            <span className="status">Lagret i skyen</span>
                            <span className="timestamp">{metadata.timestamp ? parseDateToTimestamp(new Date(metadata.timestamp)) : null}</span>
                        </div>
                    </div>
                </div>
                <div className="editorToolbar">
                    <EditorHistoryTray context={EditorContext}/>
                    <EditorHeadingTray context={EditorContext}/>
                    <EditorFormatTray context={EditorContext}/>
                    <EditorColorsTray context={EditorContext}/>
                    <EditorTextAlignTray context={EditorContext}/>
                    <EditorListTray context={EditorContext}/>
                    <EditorTableTray context={EditorContext}/>
                    
                </div>
                {
                    !loading ?
                    <div className="editorContent">
                        <EditorContent editor={editor}/>
                        <DragHandle
                            editor={editor}
                            computePositionConfig={{
                                placement: 'left-start',
                                strategy: 'fixed',
                            }}
                        >
                            <DragHandleIcon className="dragHandleIcon"/>
                        </DragHandle>
                    </div>
                    : null
                }
            </StyledNoteEditor>
        </EditorContext.Provider>
    )
}

const StyledNoteEditor = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    div.editorHeader
    {
        width: 100%;
        display: flex;
        gap: ${({theme}) => theme.spacing.lg};
        padding: ${({theme}) => theme.spacing.md};
        background-color: ${({theme}) => theme.colors.neutralSecondaryBackground};
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};

        div.headerNoteTitle
        {
            flex: 1;

            > div
            {
                color: ${({theme}) => theme.colors.mutedForeground};
            }

            > input
            {
                border: none;
                padding: ${({theme}) => theme.spacing.xs} 0;
                outline: none;
                background: none;
                font-size: ${({theme}) => theme.fontSize.lg};
                font-weight: 600;
                width: 100%;
            }
        }

        div.cloudInfo
        {
            display: flex;
            align-items: center;
            gap: ${({theme}) => theme.spacing.md};
            padding: 0 ${({theme}) => theme.spacing.md};

            svg
            {
                color: ${({theme}) => theme.colors.mutedForeground};
            }

            > div
            {
                display: flex;
                flex-direction: column;
                gap: ${({theme}) => theme.spacing.sm};

                span.status
                {
                    color: ${({theme}) => theme.colors.mutedForeground};
                }

                span.timestamp
                {
                    font-weight: 500;
                }
        
            }
        }
    }

    div.editorToolbar
    {
        display: flex;
        width: 100%;
        //gap: ${({theme}) => theme.spacing.lg};
        background-color: ${({theme}) => theme.colors.neutralSecondaryBackground};
        padding: ${({theme}) => theme.spacing.md};
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    }

    div.editorContent
    {
        width: 100%;
        max-width: ${({theme}) => theme.breakPoints.md};
        flex: 1 1 auto;
        padding: ${({theme}) => theme.spacing.xxl} 0;
        display: flex;
        overflow: hidden;

        svg.dragHandleIcon
        {
            height: 20px;
            width: 20px;
            fill: ${({theme}) => theme.colors.mutedForeground};
            cursor: grab;
            transition-duration: 110ms;
        }

        > div
        {
            width: 100%;
        }
    }
`